import type {GetStaticProps} from 'next'
import {createClient} from 'prismicio'
import type {
  ArticleDocument,
  ButtonDocument,
  CustomerStoryDocument,
  HomePageDocument,
  NavigationDocumentData,
  ProductDeepDiveDocumentData,
} from 'prismicio-types'
import {Home} from 'src/Home'
import {BUTTON_FETCH_LINKS, NAVIGATION_FETCH_LINKS, NAVIGATION_QUERY} from 'src/common/constants'
import {logger} from 'src/common/logger'
import {fetchArticlesIfSlicePresent} from 'src/common/utils/articleRoutes'
import {
  fetchCustomerStoriesData,
  fetchCustomerStoriesIfSlicePresent,
  resolveCustomerStoriesV2Links,
} from 'src/common/utils/customerStoryRoutes'
export default Home

export const getStaticProps: GetStaticProps<{
  navigationData: NavigationDocumentData
  page: HomePageDocument
  productDeepDiveData: ProductDeepDiveDocumentData | null
  buttonsData: ButtonDocument[]
  articles?: ArticleDocument[]
  customerStories?: CustomerStoryDocument[]
  customerStoriesData?: CustomerStoryDocument[]
}> = async ({locale, previewData}) => {
  const client = createClient({previewData})
  const productDeepDiveData = null

  const [navigationData, page, buttonsData] = await Promise.all([
    client.getSingle('navigation', {
      fetchLinks: NAVIGATION_FETCH_LINKS,
      lang: locale,
      graphQuery: NAVIGATION_QUERY,
    }),
    client.getSingle('home_page', {lang: locale}),
    client.getAllByType<ButtonDocument>('button', {lang: locale, fetchLinks: BUTTON_FETCH_LINKS}),
  ])
    .then(([navigation, homePage, buttons]) => [navigation.data, homePage, buttons as ButtonDocument[]] as const)
    .catch((error) => {
      logger.error('Failed to load page data from Prismic', {route: 'index', locale}, error)
      throw new Error('Unable to get data from Prismic', {cause: error})
    })

  const pageSlices = (await resolveCustomerStoriesV2Links(client, page.data.slices)) as typeof page.data.slices

  const slices = pageSlices as Array<{slice_type: string}>

  const [articles, customerStories, customerStoriesData] = locale
    ? await Promise.all([
        fetchArticlesIfSlicePresent(client, slices, locale, ['latest_articles'], 3),
        fetchCustomerStoriesIfSlicePresent(client, slices, locale),
        fetchCustomerStoriesData(client, locale),
      ])
    : [[], [], []]

  return {
    props: {
      navigationData,
      page: {...page, data: {...page.data, slices: pageSlices}},
      productDeepDiveData,
      buttonsData,
      ...(articles.length > 0 ? {articles} : {}),
      ...(customerStories.length > 0 ? {customerStories} : {}),
      ...(customerStoriesData.length > 0 ? {customerStoriesData} : {}),
    },

    revalidate: Number(process.env.REVALIDATE_TIME) || 300,
  }
}
